import { classifyError } from '@saay/api-client';
import { useAuth } from '@saay/auth-web';
import { CardContent, ListRow, StatusBadge, Surface } from '@saay/blocks';
import { Alert, AlertDescription } from '@saay/shadcn/components/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@saay/shadcn/components/alert-dialog';
import { Button } from '@saay/shadcn/components/button';
import { cn } from '@saay/shadcn/lib/utils';
import { MonitorIcon, SmartphoneIcon, type LucideIcon } from 'lucide-react';
import { useEffect, useState } from 'react';

import { QueryState } from '../components/guard.js';
import { DARK_PILL } from '../components/surface-section.js';
import {
  CommandFeedback,
  EmptyState,
  ErrorState,
  PageHeader,
  Skeleton,
  useOnlineStatus,
} from '../kit.js';
import {
  formatRelativeSolarHijri,
  sessionChannelLabel,
  signedOutOthersFa,
  useAdminSessions,
  useEndAdminSession,
  useSignOutOtherSessions,
  type AdminSession,
} from '../sessions.js';

type Confirm =
  { readonly kind: 'one'; readonly sessionId: string } | { readonly kind: 'others' } | null;

function channelIcon(channel: string): LucideIcon {
  return channel === 'BUYER_MOBILE' ? SmartphoneIcon : MonitorIcon;
}

function useNow(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(timer);
  }, [intervalMs]);
  return now;
}

/**
 * `/sessions` «دستگاه‌های فعال» (AC §27.1). The current row has no «خروج»: ending this device is the
 * app's normal logout, so `409 SESSION_IS_CURRENT` is not requested.
 */
export function SessionsPage() {
  const isOnline = useOnlineStatus();
  const nowMs = useNow(60_000);
  const sessions = useAdminSessions();
  const endSession = useEndAdminSession();
  const signOutOthers = useSignOutOtherSessions();
  const { controller } = useAuth();
  const [confirm, setConfirm] = useState<Confirm>(null);
  const items = sessions.data?.items ?? [];
  const othersExist = items.some((item) => !item.current);
  const commandError = endSession.error ?? signOutOthers.error;
  const busy = endSession.isPending || signOutOthers.isPending;

  function retryCommand(): void {
    if (endSession.isError && endSession.variables !== undefined) {
      endSession.mutate(endSession.variables);
      return;
    }
    if (signOutOthers.isError) {
      signOutOthers.mutate();
    }
  }

  async function confirmAction(): Promise<void> {
    if (confirm === null || !isOnline) {
      return;
    }
    try {
      if (confirm.kind === 'one') {
        await endSession.mutateAsync(confirm.sessionId);
      } else {
        await signOutOthers.mutateAsync();
      }
    } catch {
      // The mutation keeps the error; the page shows it with retry.
    }
    setConfirm(null);
  }

  return (
    <>
      <PageHeader title="دستگاه‌های فعال" />
      <QueryState
        empty={<EmptyState icon="shield" title="دستگاه فعالی نیست" />}
        error={sessions.error}
        isEmpty={sessions.data !== undefined && items.length === 0}
        onRetry={() => void sessions.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری دستگاه‌ها…" lines={4} />}
        status={sessions.status}
      >
        <Surface aria-label="دستگاه‌های فعال" className="py-3" role="region">
          <CardContent className="flex flex-col gap-2 px-3">
            {items.map((item) => (
              <SessionRow
                item={item}
                key={item.id}
                nowMs={nowMs}
                onEnd={() => {
                  endSession.reset();
                  signOutOthers.reset();
                  setConfirm({ kind: 'one', sessionId: item.id });
                }}
                onLogout={() => void controller.logout()}
              />
            ))}
          </CardContent>
        </Surface>
        {signOutOthers.isSuccess && signOutOthers.data !== undefined ? (
          <Alert role="status">
            <AlertDescription>{signedOutOthersFa(signOutOthers.data.endedCount)}</AlertDescription>
          </Alert>
        ) : null}
        {commandError === null ? null : classifyError(commandError) === 'unknown-result' ? (
          <CommandFeedback
            onCheckStatus={retryCommand}
            state={{ error: commandError, status: 'unknown' }}
          />
        ) : (
          <ErrorState error={commandError} onRetry={retryCommand} title="ثبت انجام نشد" />
        )}
        {othersExist ? (
          <Button
            className={cn(DARK_PILL, 'w-full')}
            disabled={!isOnline || busy}
            onClick={() => {
              endSession.reset();
              signOutOthers.reset();
              setConfirm({ kind: 'others' });
            }}
            size="lg"
            type="button"
          >
            خروج از همهٔ دستگاه‌های دیگر
          </Button>
        ) : null}
      </QueryState>
      <AlertDialog
        onOpenChange={(open) => {
          if (!open && !busy) {
            setConfirm(null);
          }
        }}
        open={confirm !== null}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.kind === 'others'
                ? 'از همهٔ دستگاه‌های دیگر خارج شوید؟'
                : 'از این دستگاه خارج شود؟'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.kind === 'others'
                ? 'از این دستگاه خارج نمی‌شوید.'
                : 'این دستگاه باید دوباره وارد شود.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy || !isOnline} type="button">
              انصراف
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={busy || !isOnline}
              onClick={() => void confirmAction()}
              type="button"
              variant="destructive"
            >
              {confirm?.kind === 'others' ? 'خروج از همهٔ دستگاه‌های دیگر' : 'خروج'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function SessionRow({
  item,
  nowMs,
  onEnd,
  onLogout,
}: {
  readonly item: AdminSession;
  readonly nowMs: number;
  readonly onEnd: () => void;
  readonly onLogout: () => void;
}) {
  const label = sessionChannelLabel(item.channel);
  return (
    <ListRow
      description={
        <>
          <span>{formatRelativeSolarHijri(item.lastAccessAt, nowMs)}</span>
          {' · '}
          <bdi dir="ltr">{item.ipAddressMasked}</bdi>
        </>
      }
      end={
        item.current ? (
          <span className="flex flex-col items-end gap-1.5 sm:flex-row sm:items-center sm:gap-2">
            <StatusBadge tone="olive">این دستگاه</StatusBadge>
            <Button onClick={onLogout} size="sm" type="button" variant="outline">
              خروج از حساب
            </Button>
          </span>
        ) : (
          <Button onClick={onEnd} size="sm" type="button" variant="outline">
            خروج
          </Button>
        )
      }
      icon={channelIcon(item.channel)}
      look="tile"
      title={label}
      tone="forest"
    />
  );
}
